import React, { Suspense, useCallback, useEffect, useState, useRef, useMemo } from 'react'
import { useZoomPan, useOverlay, useInfoSheet, InfoSheet, usePageWidth, Separator } from '@fs/zion-ui'
import { Title } from '@fs/zion-head'
import { NoticeLoading } from '@fs/zion-icon'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { usePageView } from '@fs/zion-analytics'
import { useQueryParams } from '@fs/zion-router'
import { ComboViewer, RibbonViewer, SingleImageViewer, StaticImageViewer } from '@fs/zion-image-viewer'
import { Usabilla } from '@fs/zion-usabilla'
import { getUser } from '@fs/zion-user'
import { encodeArk } from '@fs/zion-ark'
import { useFeatureFlag } from '@fs/zion-flags'
import { splunk } from '@fs/zion-splunk-logger'
import zionDebug from '@fs/zion-debug'
import {
  addTypeDataToElements,
  getHighlights,
  getNestedSubelementsAndFloatingElementsByType,
  getRecordHighlights,
  processSlsElements,
  SlsHighlightsContextProvider,
  useSlsDispatchContext,
  useSlsElements,
  useSlsHighlights,
  useSlsStateContext,
  useSlsTypes,
} from '@fs/zion-sls'
import useTasks from '../../hooks/useTasks'
import { goBackToGive, getImagePermission } from '../../utils/reviewUtils'
import FeedbackOverlay from '../../../shared/components/feedbackOverlay/FeedbackOverlay'
import SlsHighlightsBase from '../../components/slsHighlightsBase/SlsHighlightsBase'
import ImageHeaderBar from '../../components/sharedReviewComponents/imageHeaderBar/ImageHeaderBar'
import StepOne from './components/reviewPanel/StepOne'
import StepOnePartTwo from './components/reviewPanel/StepOnePartTwo'
import StepTwo from './components/reviewPanel/StepTwo'
import StepThree from './components/reviewPanel/StepThree'
import StepFour from './components/reviewPanel/StepFour'
import FinishedOverlay from './components/finishedOverlay/FinishedOverlay'
import WelcomeOverlay from './components/welcomeOverlay/WelcomeOverlay'
import AddNewPerson from './components/addNewPerson/AddNewPerson'
import EditPerson from './components/editPerson/EditPerson'
import AddNewEvent from './components/addNewEvent/AddNewEvent'
import AddFloatingEvent from './components/addFloatingEvent/AddFloatingEvent'
import EditEvent from './components/editEvent/EditEvent'
import ReviewPersonEventsPanel from './components/reviewPersonEventsPanel/ReviewPersonEventsPanel'
import { saveNotAName, taskCompleted } from '../../../shared/services/recordEnhancement/recordEnhancementService'
import { useFamilyReviewStateContext, useFamilyReviewDispatchContext, STEPS } from './providers/FamilyReviewContext'
import { getZoom, getPan } from '../../utils/zoomPanUtils'
import { useR2Highlights } from '../../providers/r2Provider/R2Context'
import ErrorOverlay from '../../../shared/components/errorOverlay/ErrorOverlay'
import NoRecordsOnTaskOverlay from './components/noRecordsOnTaskOverlay/NoRecordsOnTaskOverlay'
import { getPrimaryOrFirstPerson, getAllPersons, hasPersonsOnTwoImages } from './utils/utils'
import { showEventsHighlights, showEssentialInfoHighlights } from './utils/highlightUtils'
import { headers, FamilyReviewApp } from '../../../shared/services/config'
import { getImageArkUrl } from '../../../shared/services/das/dasService'
import EditHighlights from './components/editHighlights/EditHighlights'
import { onRectangleDrawn } from '../../utils/imageHighlightUtils'
import AdvancedRecordDetectionModal, {
  useAdvancedRecordDetectionModalDetails,
} from './components/advancedRecordDetectionModal/AdvancedRecordDetectionModal'
import { getRecordPermissions } from '../../../shared/services/platform/platformService'
import SplitScreenContainer from '../../components/splitScreenContainer/SplitScreenContainer'
import saveSls from '../../utils/taskSaveUtils'
import usePrisonAccess from '../../../Give/hooks/usePrisonAccess'

const debug = zionDebug('FamilyReview')

const layoutCss = css`
  display: flex;
  flex-direction: column;
  // This fixes the header and the bottom docked infosheet covering the image viewer
  flex-grow: 1;
`

/**
 * Game Plan:
 * 1. Keep users changes as much as possible
 *    example: If a user makes changes in panel 3 and they go back to a previous panel and make more changes, we need to reset panel 3 with the changes just made in 2.
 * 2. Keep all panels in memory all the time (not necessarily in the DOM). This means no useEffects.
 * 3. Each panel has it's own state object in the reducer. That way any panel can be reset from anywhere.
 * 4. Save as you go as much as possible in case a user leaves.
 * 5. When a panel becomes visible, we will have a single useEffect that can do stuff.
 * 6. We are going to implement the topMostPanel --- Patrick
 * 7. After every save to SLS and SLSreducer, we need to regenerate the tasks so they will be up-to-date between panels
 */

// use the context, return the value = h

const FamilyReview = () => {
  usePageView({ page_channel: 'RIP', page_detail: 'Family Review' })
  const { query } = useQueryParams()
  const { task: taskTypeParam, placeCode: placeCodeStr, debugArk } = query
  const usingDebugArk = useMemo(() => Boolean(debugArk), [debugArk])
  const placeCode = parseInt(placeCodeStr, 10)
  const [t] = useTranslation()
  const state = useFamilyReviewStateContext()
  const dispatch = useFamilyReviewDispatchContext()
  const zionSlsState = useSlsStateContext()
  const zionSlsDispatch = useSlsDispatchContext()
  const {
    displayHighlights,
    displayHighlightsApi,
    splitScreenDisplayHighlights,
    splitScreenDisplayHighlightsApi,
    recordDisplayHighlights,
    recordDisplayHighlightsApi,
    splitScreenRecordDisplayHighlights,
    splitScreenRecordDisplayHighlightsApi,
  } = useR2Highlights()
  const [{ controllerRef, stageRef, viewerRef }, { set: setZoomPan }] = useZoomPan()
  const [splitScreenProps, { set: setSplitScreenZoomPan }] = useZoomPan()
  const shared_dasProxy = useFeatureFlag('shared_dasProxy')
  const advancedRecordDetectionFlag = useFeatureFlag('rip_familyReview_advancedRecordDetection')
  const feedbackFlag = useFeatureFlag('rip_sharedImageViewer_feedback_FamilyReview')
  const saveChangesFlag = useFeatureFlag('rip_sharedImageViewer_saveChanges')
  const [saveUpdateCalled, _setSaveUpdateCalled] = useState(false)
  const setSaveUpdateCalled = useCallback(
    (bool) => {
      // Any save action will change the default user action, "reviewed" to "fixed"
      if (bool) dispatch({ type: 'SET_STATS_TRACKING_USER_ACTION', value: 'fixed' })
      _setSaveUpdateCalled(bool)
    },
    [dispatch, _setSaveUpdateCalled]
  )
  const [hasCheckedImagePermission, setHasCheckedImagePermission] = useState(false)
  const atPageWidth = usePageWidth()
  const isSmallScreen = atPageWidth({ default: true, md: false })
  const { accessFromPrison, isReady } = usePrisonAccess()

  const h = useSlsHighlights({
    mainDisplay: displayHighlights,
    mainApi: displayHighlightsApi,
    splitScreenDisplay: splitScreenDisplayHighlights,
    splitScreenApi: splitScreenDisplayHighlightsApi,
    mainRecordDisplay: recordDisplayHighlights,
    mainRecordApi: recordDisplayHighlightsApi,
    splitScreenRecordDisplay: splitScreenRecordDisplayHighlights,
    splitScreenRecordApi: splitScreenRecordDisplayHighlightsApi,
    setZoomPanCb: setZoomPan,
    setSplitScreenZoomPanCb: setSplitScreenZoomPan,
  })

  // Splitscreen state
  const [splitScreenColumnCount, setSplitScreenColumnCount] = useState(3)
  const [associatedImages, setAssociatedImages] = useState([])
  const splitScreenImages = useMemo(() => {
    return associatedImages.map(({ signedUrl, apid }) => {
      return {
        src: signedUrl,
        alt: apid,
        id: apid,
      }
    })
  }, [associatedImages])
  const associatedImageArks = useMemo(() => {
    if (associatedImages.length) return associatedImages.map(({ partitionKey }) => partitionKey)
    return []
  }, [associatedImages])

  const { data: dataTypes } = useSlsTypes({
    fsUserAgentChain: headers['FS-User-Agent-Chain'],
    dispatchTypes: true,
    lang: 'en',
  })

  const { loading: splitScreenSlsLoading } = useSlsElements({
    imageArks: associatedImageArks,
    fsUserAgentChain: headers['FS-User-Agent-Chain'],
    sort: true,
    types: dataTypes,
    dispatchImages: true,
    enableHook: true,
  })

  const [primaryPersonId, setPrimaryPersonId] = useState(null)

  // / Zion overlays and refs / //
  const infoSheetOverlay = useInfoSheet({ initiallyOpen: true })
  const finishedOverlay = useOverlay()
  const welcomeOverlay = useOverlay()
  const stepOnePartTwoOverlay = useOverlay()
  const stepTwoOverlay = useOverlay()
  const stepThreeOverlay = useOverlay()
  const stepFourOverlay = useOverlay()
  const addNewPersonOverlay = useOverlay()
  const addNewEventOverlay = useOverlay()
  const addFloatingEventOverlay = useOverlay()
  const editHighlightsOverlay = useOverlay()
  const editPersonOverlay = useOverlay()
  const reviewPersonEventsOverlay = useOverlay()
  const editEventOverlay = useOverlay()
  const noRecordsOnTaskOverlay = useOverlay()
  const advancedRecordDetectionOverlay = useOverlay()

  // Retrieve external data
  useEffect(() => {
    getUser().then((u) => dispatch({ type: 'SET_USER_ID', value: u.cisId }))
  }, [dispatch])

  const getUrl = useCallback(
    (partitionKey, artifactUrl) => {
      return artifactUrl || getImageArkUrl(partitionKey, shared_dasProxy.isOn) // fallback to unsigned url if necessary
    },
    [shared_dasProxy.isOn]
  )
  const setError = useCallback(
    (value) => {
      dispatch({ type: 'SET_ERROR', value })
    },
    [dispatch]
  )

  const doNotShowAdvancedRecordDetectionModal = localStorage.getItem('rip:doNotShowAdvancedRecordDetectionModal')
  const { hasShownModalForRecordId, setHasShownModalForRecordId } = useAdvancedRecordDetectionModalDetails()

  const currentTaskUpdated = useCallback(
    ({ newTask, imageHighlights, recordHighlights }) => {
      // otherArks related to this task need to be discovered now (this is the soonest it can happen), because there is the potential that a splitscreen image will be loaded from that discovery
      if (advancedRecordDetectionFlag.isOn && hasPersonsOnTwoImages(newTask))
        if (doNotShowAdvancedRecordDetectionModal === null && !hasShownModalForRecordId({ recordId: newTask?.id })) {
          advancedRecordDetectionOverlay.open()
          setHasShownModalForRecordId({ recordId: newTask?.id })
        }
      // Setting this for splitscreen
      setPrimaryPersonId(getPrimaryOrFirstPerson(newTask)?.id)
      showEssentialInfoHighlights({
        h,
        mainPartitionKey: newTask.partitionKey,
        splitScreenPartitionKey: state.splitScreenPartitionKey,
        personIds: [getPrimaryOrFirstPerson(newTask)?.id],
        highlights: imageHighlights,
        recordHighlights,
        isSmallScreen,
      })
      // Set selected person id
      dispatch({ type: 'SET_SELECTED_PERSON_ID', value: getPrimaryOrFirstPerson(newTask)?.id })

      // reset panels
      const allPersons = getAllPersons(newTask)
      dispatch({
        type: 'SET_STEP_TWO_CARD_REVIEWS',
        value: allPersons.map((person) => {
          return { personId: person?.id, hasBeenReviewed: false }
        }),
      })
      const eventCards = []
      const events = newTask.subElements
        ?.filter((se) => se.elementType === 'PERSON')
        .flatMap((person) => person?.subElements?.filter((el) => el.elementType === 'EVENT'))
      events?.forEach((event) => {
        if (!eventCards.find((card) => card.eventId === event?.id))
          eventCards.push({ eventId: event?.id, hasBeenReviewed: false })
      })
    },
    [
      h,
      dispatch,
      isSmallScreen,
      doNotShowAdvancedRecordDetectionModal,
      advancedRecordDetectionFlag.isOn,
      advancedRecordDetectionOverlay,
      state.splitScreenPartitionKey,
      hasShownModalForRecordId,
      setHasShownModalForRecordId,
    ]
  )

  const artifactId = useRef(null)
  const artifactUrl = useRef(null)
  const [hasImagePermission, setHasImagePermission] = useState(false)
  const [hasRecordPermission, setHasRecordPermssion] = useState(false)

  const checkImagePermission = async (recordElements) => {
    getImagePermission(recordElements, FamilyReviewApp).then((permission) => {
      setHasImagePermission(permission)
      setHasCheckedImagePermission(true)
    })
  }

  const getAdvancedRecordDetectionChoice = () => {
    return localStorage.getItem('rip:advancedRecordDetectionChoice')
  }
  const choice = getAdvancedRecordDetectionChoice()

  /**
   * The generateTasks function behaves in one of two ways:
   *  1. If the "data" object is passed in, it will call processSlsElements with the tasks from the "task" API call, and then set the images property as if it were passed in.
   *  2. If the "images" object is passed in, it will skip that step and go ahead updating the tasks state with the current image state.
   */
  const [associatedImagesStateLimbo, setAssociatedImagesStateLimbo] = useState([])
  const generateTasks = useCallback(
    ({ data, images = [], currentIndex = 0 }) => {
      // Set splitscreen image data
      if (data?.associatedImages) setAssociatedImagesStateLimbo(data.associatedImages)
      // Process the data into an images array format
      if (data?.slsImageInfo && data?.artifactId) {
        artifactId.current = data.artifactId
        artifactUrl.current = data.artifactUrl

        images = processSlsElements([
          {
            elements: data.slsImageInfo.elements,
            danglingSupers: data.slsImageInfo.danglingSupers,
            danglingElementTypes: data.slsImageInfo.danglingElementTypes,
            danglingElementDescriptions: data.slsImageInfo.danglingElementDescriptions,
            partitionKey: encodeArk(artifactId.current),
          },
        ]).images
      }

      // Add typeData to elements
      addTypeDataToElements(images, dataTypes, t('type-unknown.label', '<Unknown Type>'))
      images.forEach((img) => {
        addTypeDataToElements(img.floatingElements, dataTypes, t('type-unknown.label', '<Unknown Type>'))
      })

      /**
       * GAME PLAN:
       * 1. Give every RECORD element attribution.
       * 1.1 The only exception is if the user prefers to skip advanced records. We'll skip them, but intentionally NOT add attribution
       *     so an an advanced user can get the advanced set of records for the same image at a later time.
       * 2. The RECORD elements that don't qualify for this experience will be skipped.
       * 3. Skipped elements will be given automatic attribution so the image doesn't show up again
       * 3. Filter the following RECORD elements:
       *  - If the record has no persons
       *  - If the record has only dangling persons
       */
      const recordsToSkip = []
      const outRecordsWithoutPersons = (record) => {
        const persons = record.subElements?.filter((se) => se.elementType === 'PERSON')
        if (!persons?.length) {
          recordsToSkip.push(record)
          return false
        }
        return true
      }
      const outRecordsWithOnlyDanglingPersons = (record) => {
        const persons = record.subElements?.filter((se) => se.elementType === 'PERSON')
        const nonDanglingPersons = persons?.filter((se) => !se.isDangling)
        const danglingPersons = persons?.filter((se) => se.isDangling)
        if (!nonDanglingPersons.length && danglingPersons.length) {
          recordsToSkip.push(record)
          return false
        }
        return true
      }
      const outAdvancedRecordsIfUserWantsTo = (record) => {
        const danglingPersons = record.subElements
          .filter((se) => se.elementType === 'PERSON')
          .filter((se) => se.isDangling)

        if (danglingPersons?.length > 0) {
          if (choice === 'skip' && advancedRecordDetectionFlag.isOn) {
            console.error('This image contains records that have persons on two images. These persons will be skipped.')
            return false // Skip all advanced records
          }
        }
        return true
      }
      const outRecordsWithAttribution = (record) => {
        if (usingDebugArk) return true // Don't do this if we're using a debug ark
        return !record.reviewAttribution?.userId?.includes('cis')
      }
      const recordElements = getNestedSubelementsAndFloatingElementsByType(images[0], 'RECORD')
      const filteredRecordElements = recordElements
        .filter(outRecordsWithoutPersons)
        .filter(outRecordsWithOnlyDanglingPersons)
        .filter(outAdvancedRecordsIfUserWantsTo)
        .filter(outRecordsWithAttribution)
        .map((record) => {
          return { ...record, artifactUrl: getUrl(record.partitionKey, artifactUrl.current) }
        })

      // Add attribution to skipped records
      if (recordsToSkip.length) {
        const skippedRecordDispatches = []
        recordsToSkip.forEach((record) => {
          skippedRecordDispatches.push({
            type: 'SET_REVIEW_ATTRIBUTION',
            value: {
              partitionKey: record.partitionKey,
              id: record?.id,
              elementType: 'RECORD',
              sequenceNumber: 1,
              attributionArg: {},
            },
          })
        })
        const saveSkippedRecords = async () => {
          try {
            await saveSls({
              dispatches: skippedRecordDispatches,
              updateState: true,
              userId: state.userId,
              taskTypeParam: 'Family Review',
              appName: FamilyReviewApp,
              saveChangesFlag,
              zionSLSState: zionSlsState,
              zionSlsDispatch,
            })
          } catch (e) {
            splunk.log('Failed to save skipped Family Review records.', e)
          }
        }
        saveSkippedRecords()
      }

      // Check that we have elements
      if (filteredRecordElements.length) {
        // Get all highlights from the image and set them in FamilyReview state
        const imageHighlights = getHighlights({ element: images[0] })
        const mainRecordHighlights = getRecordHighlights(images[[0]])
        h.setHighlights({
          mainHighlights: imageHighlights,
          mainRecordHighlights,
          setStaticCopy: true,
          setEditCopy: true,
        })

        const ark = encodeArk(artifactId.current)
        if (!hasCheckedImagePermission) checkImagePermission(filteredRecordElements)
        zionSlsDispatch({
          type: 'SET_DATA',
          value: {
            images,
            currentImageArk: ark,
          },
        })

        currentTaskUpdated({
          newTask: filteredRecordElements[currentIndex],
          imageHighlights,
          recordHighlights: mainRecordHighlights,
        })
        debug('Family Review Records: ', { recordElements, filteredRecordElements })

        // This return value gives the array of tasks to the useTasks hook
        return filteredRecordElements
      }
      debug('Family Review Records: ', { recordElements, filteredRecordElements })
      const callTaskCompleted = async () => {
        try {
          await taskCompleted(artifactId.current, true, 'Family Review')
        } catch (e) {
          console.error('taskCompleted failed.', e)
        }
      }
      callTaskCompleted()
      return noRecordsOnTaskOverlay.handleClick()
    },
    [
      saveChangesFlag,
      state.userId,
      zionSlsState,
      usingDebugArk,
      advancedRecordDetectionFlag.isOn,
      choice,
      currentTaskUpdated,
      dataTypes,
      getUrl,
      h,
      hasCheckedImagePermission,
      noRecordsOnTaskOverlay,
      t,
      zionSlsDispatch,
    ]
  )

  const onGoalCompleted = useCallback(() => {
    finishedOverlay.handleClick() // Open the finished overlay
  }, [finishedOverlay])

  const {
    continueWithNewGoal,
    goToNextTask,
    skipTask,
    updateTaskList,
    fetchTaskData,
    currentTask,
    loading,
    currentIndex,
  } = useTasks({
    currentTaskUpdated,
    generateTasks,
    goalDefault: 1,
    goalCacheKey: 'familyReviewTaskLengthPreference',
    onGoalCompleted,
    setError: (value) => dispatch({ type: 'SET_ERROR', value }),
    types: dataTypes,
  })

  /**
   * This useEffect has two main purposes:
   *  1. Postpone the request for splitscreen images until the first image is set in zionSlsState
   *  2. Run the setup code for the splitscreen image once the data is available from having loaded splitscreen
   */
  const [splitScreenRibbonReady, setSplitScreenRibbonReady] = useState(false)
  useEffect(() => {
    // This postpones the request for splitscreen images
    if (zionSlsState.images.length && associatedImagesStateLimbo.length && !associatedImages.length) {
      setAssociatedImages(associatedImagesStateLimbo)
    }

    // Setup the splitscreen image once the data is available
    if (
      associatedImages.length &&
      !splitScreenSlsLoading &&
      zionSlsState.images.length &&
      splitScreenRibbonReady === false &&
      currentTask?.partitionKey
    ) {
      const arbitrarilySelectedImageArk = associatedImages[0].partitionKey
      dispatch({ type: 'SET_SPLIT_SCREEN_PARTITION_KEY', value: arbitrarilySelectedImageArk })
      dispatch({ type: 'SET_SPLIT_SCREEN_FOCUSED_INDEX', value: 0 })
      const { splitScreenHighlights } = h.init({ splitScreenImageArk: arbitrarilySelectedImageArk })
      const { mainEditHighlights } = h.getHighlights()
      showEssentialInfoHighlights({
        h,
        personIds: [primaryPersonId],
        highlights: [...splitScreenHighlights, ...mainEditHighlights],
        isSmallScreen,
        mainPartitionKey: currentTask.partitionKey,
        splitScreenPartitionKey: arbitrarilySelectedImageArk,
      })
      const newTaskList = generateTasks({ images: zionSlsState.images, currentIndex })
      updateTaskList({ newTaskList })
      setSplitScreenRibbonReady(true)
    }
  }, [
    h,
    associatedImages,
    associatedImagesStateLimbo,
    dispatch,
    currentTask,
    zionSlsState,
    isSmallScreen,
    primaryPersonId,
    splitScreenSlsLoading,
    splitScreenRibbonReady,
    splitScreenDisplayHighlightsApi,
    zionSlsDispatch,
    generateTasks,
    updateTaskList,
    currentIndex,
  ])

  // it uses the cache and not hit the wire every time
  getRecordPermissions(zionSlsState?.images, currentTask?.id).then((permission) => {
    setHasRecordPermssion(permission)
  })

  const handleOpenFinishedOverlay = () => {
    finishedOverlay.handleClick()
  }

  const updateStepTwoData = useCallback(
    (relationshipCards, nextRelationshipCard, highlights) => {
      dispatch({ type: 'RESET_STEP_TWO' })
      dispatch({
        type: 'SET_STEP_TWO_DATA',
        value: { relationshipCards, selectedPersonId: nextRelationshipCard.personId },
      })
      showEssentialInfoHighlights({
        h,
        highlights,
        mainPartitionKey: currentTask.partitionKey,
        splitScreenPartitionKey: state.splitScreenPartitionKey,
        personIds: [nextRelationshipCard?.personId],
        isSmallScreen,
      })
    },
    [currentTask, dispatch, isSmallScreen, h, state.splitScreenPartitionKey]
  )

  const updateStepThreeData = useCallback(
    (eventCards, nextEventCard, highlights) => {
      dispatch({ type: 'RESET_STEP_THREE' })
      dispatch({
        type: 'SET_STEP_THREE_DATA',
        value: { eventCards, selectedEventId: nextEventCard?.eventId },
      })
      showEventsHighlights({
        h,
        currentTask,
        highlights: highlights || [
          ...h.getHighlights().mainEditHighlights,
          ...h.getHighlights().splitScreenEditHighlights,
        ],
        eventIds: [nextEventCard?.eventId],
        isSmallScreen,
        splitScreenPartitionKey: state.splitScreenPartitionKey,
      })
    },
    [currentTask, dispatch, isSmallScreen, h, state]
  )

  /**
   * This is good for resetting StepTwo and StepThree. It makes sure to keep the hasBeenReviewed prop
   * @param {Object} props:
   *                    elements - {Array} the array of event or person elements
   *                    stepStateCards - {Array} either state.stepTwo.relationshipCards or state.stepThree.eventCards
   *                    key - {String} either 'personId' or 'eventId'
   *                    updateStepData - {Function} how the step sends off its dispatches
   * @returns {Array} list of persons sorted by primary first
   */
  const setCardState = useCallback(
    ({ elements, stepStateCards, key, updateStepData, markThisCardCorrect, highlights }) => {
      const cards = []
      if (elements) {
        elements.forEach((element) => {
          if (!cards.find((card) => card.eventId === element?.id))
            cards.push({
              [key]: element?.id,
              hasBeenReviewed:
                element?.id === markThisCardCorrect ||
                stepStateCards.find((card) => card[key] === element?.id)?.hasBeenReviewed ||
                false,
            })
        })
        // make sure the next card is highlighted
        const nextEventCard = cards.find((card) => !card.hasBeenReviewed) || cards[cards.length - 1]
        updateStepData(cards, nextEventCard, highlights)
      }
    },
    []
  )

  const setStepTwoData = useCallback(
    (markThisCardCorrect, highlights) => {
      const persons = getAllPersons(currentTask)
      setCardState({
        elements: persons,
        stepStateCards: state.stepTwo.relationshipCards,
        key: 'personId',
        updateStepData: updateStepTwoData,
        markThisCardCorrect,
        highlights,
      })
    },
    [currentTask, setCardState, state.stepTwo.relationshipCards, updateStepTwoData]
  )

  const navigateToStepOne = useCallback(() => {
    stepFourOverlay.close()
    stepThreeOverlay.close()
    stepTwoOverlay.close()
    stepOnePartTwoOverlay.close()
    dispatch({ type: 'SET_CURRENT_STEP', value: STEPS.STEP_ONE })
  }, [dispatch, stepFourOverlay, stepThreeOverlay, stepTwoOverlay, stepOnePartTwoOverlay])

  const handleContinue = useCallback(() => {
    finishedOverlay.handleClick()
    navigateToStepOne()
    continueWithNewGoal(1)
  }, [finishedOverlay, continueWithNewGoal, navigateToStepOne])

  const rectangleDrawnHandler = (position) => {
    onRectangleDrawn(position, [currentTask], currentTask.partitionKey, state, displayHighlightsApi, dispatch)
  }

  const handleNewPerson = () => {
    addNewPersonOverlay.open()
    h.clearDisplay({ all: true })
  }

  const handleGoToNextRecordAndResetStep = () => {
    dispatch({ type: 'SET_CURRENT_STEP', value: STEPS.STEP_ONE })
    stepTwoOverlay.close()
    skipTask()
  }

  const getNewImage = async () => {
    noRecordsOnTaskOverlay.close()
    fetchTaskData()
  }

  const handleSkip = () => {
    skipTask()
    advancedRecordDetectionOverlay.close()
  }

  const handleProceed = () => {
    advancedRecordDetectionOverlay.close()
  }

  useEffect(() => {
    if (saveUpdateCalled) {
      const newTaskList = generateTasks({ images: zionSlsState.images, currentIndex })
      updateTaskList({ newTaskList })
      setSaveUpdateCalled(false)
    }
  }, [currentIndex, generateTasks, updateTaskList, saveUpdateCalled, zionSlsState.images, setSaveUpdateCalled])

  debug({ artifactUrl: currentTask?.artifactUrl, currentTask, state, zionSlsState, saveUpdateCalled })

  return (
    <Suspense fallback={<NoticeLoading />}>
      <SlsHighlightsContextProvider value={h}>
        <Title>{t('family-review.title', 'Family Review')}</Title>
        {currentTask && currentTask?.id && (
          <InfoSheet
            title={t('family-review.infoSheetTitle', 'Identify Primary Person')}
            {...infoSheetOverlay}
            nonClosable
            size="md"
            headingColor="purple"
            elevated
          >
            <StepOne
              currentTask={currentTask}
              loading={loading}
              setStepTwoData={setStepTwoData}
              stepTwoOverlay={stepTwoOverlay}
              stepOnePartTwoOverlay={stepOnePartTwoOverlay}
              skipTask={skipTask}
              saveNotAName={saveNotAName}
              taskType={taskTypeParam}
              hasImagePermission={hasImagePermission}
              handleGoToNextRecordAndResetStep={handleGoToNextRecordAndResetStep}
              setSaveUpdateCalled={setSaveUpdateCalled}
            />
            <StepOnePartTwo
              addNewPersonOverlay={addNewPersonOverlay}
              currentTask={currentTask}
              goToNextStep={setStepTwoData}
              stepOnePartTwoOverlay={stepOnePartTwoOverlay}
              stepTwoOverlay={stepTwoOverlay}
              editHighlightsOverlay={editHighlightsOverlay}
              setSaveUpdateCalled={setSaveUpdateCalled}
              hasImagePermission={hasImagePermission}
              handleGoToNextRecordAndResetStep={handleGoToNextRecordAndResetStep}
            />
            <StepTwo
              currentTask={currentTask}
              handleNewPerson={handleNewPerson}
              handleGoToNextRecordAndResetStep={handleGoToNextRecordAndResetStep}
              stepTwoOverlay={stepTwoOverlay}
              stepThreeOverlay={stepThreeOverlay}
              editPersonOverlay={editPersonOverlay}
              hasImagePermission={hasImagePermission}
              setCardState={setCardState}
              updateStepThreeData={updateStepThreeData}
            />
            <StepThree
              currentTask={currentTask}
              addNewEventOverlay={addNewEventOverlay}
              addFloatingEventOverlay={addFloatingEventOverlay}
              stepThreeOverlay={stepThreeOverlay}
              stepFourOverlay={stepFourOverlay}
              editEventOverlay={editEventOverlay}
              reviewPersonEventsOverlay={reviewPersonEventsOverlay}
              handleGoToNextRecord={goToNextTask}
              hasImagePermission={hasImagePermission}
            />
            <StepFour
              currentTask={currentTask}
              handleOpenFinishedOverlay={handleOpenFinishedOverlay}
              getAllPersons={getAllPersons}
              stepFourOverlay={stepFourOverlay}
              editPersonOverlay={editPersonOverlay}
            />
            <AddNewPerson
              addNewPersonOverlay={addNewPersonOverlay}
              currentTask={currentTask}
              editHighlightsOverlay={editHighlightsOverlay}
              setSaveUpdateCalled={setSaveUpdateCalled}
            />
            <AddNewEvent
              currentTask={currentTask}
              setSaveUpdateCalled={setSaveUpdateCalled}
              addNewEventOverlay={addNewEventOverlay}
              editHighlightsOverlay={editHighlightsOverlay}
              editEventOverlay={editEventOverlay}
            />
            <AddFloatingEvent
              addFloatingEventOverlay={addFloatingEventOverlay}
              addNewEventOverlay={addNewEventOverlay}
              currentTask={currentTask}
              editEventOverlay={editEventOverlay}
              setSaveUpdateCalled={setSaveUpdateCalled}
            />
            <EditPerson
              currentTask={currentTask}
              editPersonOverlay={editPersonOverlay}
              editHighlightsOverlay={editHighlightsOverlay}
              stepTwoOverlay={stepTwoOverlay}
              stepOnePartTwoOverlay={stepOnePartTwoOverlay}
              reviewPersonEventsOverlay={reviewPersonEventsOverlay}
              setSaveUpdateCalled={setSaveUpdateCalled}
              setStepTwoData={setStepTwoData}
            />
            <EditEvent
              currentTask={currentTask}
              editEventOverlay={editEventOverlay}
              stepOnePartTwoOverlay={stepOnePartTwoOverlay}
              reviewPersonEventsOverlay={reviewPersonEventsOverlay}
              editHighlightsOverlay={editHighlightsOverlay}
              setSaveUpdateCalled={setSaveUpdateCalled}
              setCardState={setCardState}
              updateStepThreeData={updateStepThreeData}
              navigateToStepOne={navigateToStepOne}
            />
            <ReviewPersonEventsPanel
              addNewEventOverlay={addNewEventOverlay}
              addFloatingEventOverlay={addFloatingEventOverlay}
              currentTask={currentTask}
              editEventOverlay={editEventOverlay}
              editPersonOverlay={editPersonOverlay}
              reviewPersonEventsOverlay={reviewPersonEventsOverlay}
            />
            <EditHighlights
              currentTask={currentTask}
              editHighlightsOverlay={editHighlightsOverlay}
              editEventOverlay={editEventOverlay}
              editPersonOverlay={editPersonOverlay}
            />
            {advancedRecordDetectionOverlay.isOpen && advancedRecordDetectionFlag.isOn && (
              <AdvancedRecordDetectionModal
                advancedRecordDetectionOverlay={advancedRecordDetectionOverlay}
                onSkip={handleSkip}
                onProceed={handleProceed}
              />
            )}
          </InfoSheet>
        )}
        <div css={layoutCss}>
          <Separator size="nano" />
          <ImageHeaderBar
            loading={loading}
            personId={getPrimaryOrFirstPerson(currentTask)?.id}
            recordDetails={hasRecordPermission}
            handwriting
            social
            welcomeOverlay={welcomeOverlay}
            title={t('family-review.title', 'Family Review')}
            setStepTwoData={setStepTwoData}
            stepTwoOverlay={stepTwoOverlay}
            stepOnePartTwoOverlay={stepOnePartTwoOverlay}
            skipTask={skipTask}
            saveNotAName={saveNotAName}
            taskType={taskTypeParam}
            hasImagePermission={hasImagePermission}
            handleGoToNextRecordAndResetStep={handleGoToNextRecordAndResetStep}
            setSaveUpdateCalled={setSaveUpdateCalled}
          />
          <Separator size="nano" />
          {currentTask?.artifactUrl && (
            <SplitScreenContainer
              appName="FamilyReview"
              mainImageTitleText={t('family-review.simple-mainImageTitle', 'Main Image')}
              splitScreenTitleText={t('family-review.splitScreenTitle', 'View Only: Image {currentIndex}', {
                currentIndex: state.splitScreenFocusedIndex + 1,
              })}
              MainImageViewer={
                <SingleImageViewer
                  {...{ controllerRef, stageRef, viewerRef }}
                  initialZoom={getZoom(h.getAttachedMainRects())}
                  initialPanX={getPan(h.getAttachedMainRects()).panX}
                  initialPanY={getPan(h.getAttachedMainRects()).panY}
                  alt=""
                  src={currentTask.artifactUrl}
                  drawingMode={state.creatingRect}
                  onRectangleDrawn={rectangleDrawnHandler}
                  prompt={
                    state.creatingRect ? t('draw-highlight.body', 'Click anywhere and drag to create a highlight') : ''
                  }
                >
                  <SlsHighlightsBase
                    showEditHighlights={editHighlightsOverlay.isOpen}
                    state={state}
                    dispatch={dispatch}
                  />
                </SingleImageViewer>
              }
              SplitScreenImageViewer={
                <ComboViewer
                  {...splitScreenProps}
                  alt=""
                  images={splitScreenImages}
                  showImageGrid={state.showImageGrid}
                  setShowImageGrid={(value) => dispatch({ type: 'SET_SHOW_IMAGE_GRID', value })}
                  focusedIndex={state.splitScreenFocusedIndex || 0}
                  setFocusedIndex={(value) => {
                    dispatch({ type: 'SET_SPLIT_SCREEN_FOCUSED_INDEX', value })
                    dispatch({ type: 'SET_SPLIT_SCREEN_PARTITION_KEY', value: associatedImages?.[value]?.partitionKey })
                  }}
                  columnCount={splitScreenColumnCount}
                  setColumnCount={setSplitScreenColumnCount}
                  ImageComponent={StaticImageViewer}
                  thinganated
                >
                  <SlsHighlightsBase
                    showEditHighlights={editHighlightsOverlay.isOpen}
                    forSplitScreen
                    state={state}
                    dispatch={dispatch}
                  />
                </ComboViewer>
              }
              splitScreenRibbonReady={splitScreenRibbonReady}
              SplitScreenRibbonViewer={
                <RibbonViewer
                  images={splitScreenImages}
                  focusedIndex={state.splitScreenFocusedIndex || 0}
                  setFocusedIndex={(value) => {
                    dispatch({ type: 'SET_SPLIT_SCREEN_FOCUSED_INDEX', value })
                    dispatch({ type: 'SET_SPLIT_SCREEN_PARTITION_KEY', value: associatedImages?.[value]?.partitionKey })
                  }}
                />
              }
              splitScreenIsOpen={advancedRecordDetectionFlag.isOn && hasPersonsOnTwoImages(currentTask)}
            />
          )}
        </div>
        <ErrorOverlay error={state.error} setError={setError} />
        <WelcomeOverlay welcomeOverlay={welcomeOverlay} />
        <FinishedOverlay
          finishedOverlay={finishedOverlay}
          placeCode={placeCode}
          currentTask={currentTask}
          handleContinue={handleContinue}
          goBackToGive={() => goBackToGive(accessFromPrison && isReady, 'familyReview')}
          hasImagePermission={hasImagePermission}
        />
        <NoRecordsOnTaskOverlay
          overlay={noRecordsOnTaskOverlay}
          goBackToGive={async () => {
            goBackToGive(accessFromPrison && isReady, 'familyReview')
          }}
          getNewImage={getNewImage}
        />
        {!isSmallScreen && <Usabilla url="//w.usabilla.com/fa948ca83eea.js" hideOnMobile mobileBreakpoint="md" />}
        {!isSmallScreen && feedbackFlag.isOn && <FeedbackOverlay />}
      </SlsHighlightsContextProvider>
    </Suspense>
  )
}

export default FamilyReview
